export interface SearchableEmoji { label: string; tags: string[] }

const words = (s: string) => s.toLowerCase().split(/[^a-z0-9\u00c0-\u024f]+/).filter(Boolean);

/**
 * Score of one emoji for a query, or -1 for no match. Every query word must match (AND), as the START of a word in the
 * name or a tag ("cat" matches "cat face" but not "communication"). Higher is better: exact name, then all-words-in-name,
 * then name-word matches, then tag matches.
 */
export function emojiScore(e: SearchableEmoji, query: string): number {
  const qs = words(query);
  if (qs.length === 0) return 0;
  const nameWords = words(e.label);
  const tagWords = e.tags.flatMap(words);
  let score = 0;
  for (const q of qs) {
    if (nameWords.includes(q)) score += 6;
    else if (nameWords.some((w) => w.startsWith(q))) score += 4;
    else if (tagWords.includes(q)) score += 3;
    else if (tagWords.some((w) => w.startsWith(q))) score += 1;
    else return -1;
  }
  if (e.label.toLowerCase() === qs.join(" ")) score += 20;
  return score;
}

/** Filters and ranks (best first, original order kept for ties). */
export function searchEmoji<T extends SearchableEmoji>(list: T[], query: string): T[] {
  if (!words(query).length) return list;
  return list
    .map((e, i) => ({ e, i, s: emojiScore(e, query) }))
    .filter((x) => x.s >= 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((x) => x.e);
}
